import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import { getCaseDetail } from "@/lib/vg2Workspace";
import DocumentSection from "./DocumentSection";

/**
 * Owner Workspace · Case detail — Phase 8A read-only view (§13, §39).
 * Opening a historical case NEVER makes it active and NEVER regenerates.
 */
export default function WorkspaceCase() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try { setView(await getCaseDetail({ caseId })); }
      catch (e) { setError(e); }
    })();
  }, [caseId]);

  if (error) {
    return (
      <Vg2Shell>
        <main className="max-w-3xl mx-auto px-4 py-8">
          <p className="text-sm text-vg2-ink mb-3" data-testid="workspace-case-error">
            {error.status === 404
              ? "Analisi non trovata o non accessibile."
              : (error.message || "Errore imprevisto.")}
          </p>
          <button onClick={() => navigate("/vg2/workspace")}
                  className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                  data-testid="workspace-case-back">
            Torna al Workspace
          </button>
        </main>
      </Vg2Shell>
    );
  }

  if (!view) {
    return (
      <Vg2Shell>
        <main className="max-w-3xl mx-auto px-4 py-8">
          <p className="text-sm text-vg2-muted" data-testid="workspace-case-loading">
            Caricamento…
          </p>
        </main>
      </Vg2Shell>
    );
  }

  return (
    <Vg2Shell>
      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="workspace-case-root">
        <button onClick={() => navigate("/vg2/workspace")}
                className="text-sm text-vg2-muted hover:text-vg2-ink underline mb-4"
                data-testid="workspace-case-back">
          ← Torna al Workspace
        </button>

        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
            {view.is_current ? "Analisi corrente" : "Analisi storica"}
          </p>
          <h1 className="font-display text-2xl font-medium mb-2"
              data-testid="workspace-case-property">
            {view.property_display_label}
          </h1>
          <p className="text-sm text-vg2-muted">
            {view.property_type_label} · Obiettivo: {view.objective_summary} ·{" "}
            {new Date(view.created_at).toLocaleDateString("it-IT")}
          </p>
        </header>

        <section className="mb-6" data-testid="workspace-case-stages">
          <h2 className="font-display text-lg font-medium mb-3">Percorso</h2>
          <ul className="divide-y divide-vg2-line border border-vg2-line rounded">
            {view.stages.map((s) => (
              <li key={s.key} className="p-3 flex items-center justify-between"
                  data-testid={`workspace-stage-${s.key}`}>
                <div>
                  <p className="text-sm font-medium">{s.label}</p>
                  <p className="text-xs text-vg2-muted">
                    {STAGE_STATUS_LABELS_IT[s.status] || s.status}
                  </p>
                </div>
                {s.read_route && (
                  <button onClick={() => navigate(s.read_route)}
                          className="text-sm underline hover:text-vg2-ink"
                          data-testid={`workspace-stage-open-${s.key}`}>
                    Apri
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>

        {view.selected_direction_label && (
          <p className="text-sm text-vg2-ink mb-3">
            <strong>Direzione scelta:</strong> {view.selected_direction_label}
          </p>
        )}
        {view.handoff_state && (
          <p className="text-sm text-vg2-ink mb-3">
            <strong>Stato Property Advisor:</strong>{" "}
            {HANDOFF_STATE_LABELS_IT[view.handoff_state] || view.handoff_state}
          </p>
        )}

        {view.involved_professionals && view.involved_professionals.length > 0 && (
          <section className="mb-6" data-testid="workspace-case-involved">
            <h2 className="font-display text-lg font-medium mb-3">
              Professionisti coinvolti
            </h2>
            <ul className="divide-y divide-vg2-line border border-vg2-line rounded">
              {view.involved_professionals.map((p, i) => (
                <li key={i} className="p-3"
                    data-testid={`workspace-involved-${i}`}>
                  <p className="text-sm font-medium">{p.display_name}</p>
                  <p className="text-xs text-vg2-muted">
                    {p.role_label} · {p.state_label}
                  </p>
                  <p className="text-xs text-vg2-ink mt-1">
                    {p.responsibility_summary}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}

        <DocumentSection variant="OWNER" caseId={view.case_id} />

        {!view.is_current && (
          <p className="text-xs text-vg2-muted mt-6"
             data-testid="workspace-case-historical-hint">
            Questa analisi è storica: viene mostrata in sola lettura.
            Per lavorare di nuovo su questo immobile, avvia una nuova
            analisi dal Workspace.
          </p>
        )}
      </main>
    </Vg2Shell>
  );
}


const STAGE_STATUS_LABELS_IT = {
  NOT_STARTED: "Non iniziato",
  IN_PROGRESS: "In corso",
  COMPLETED: "Completato",
  NEEDS_ATTENTION: "Richiede attenzione",
  STALE: "Superato da un aggiornamento",
  NOT_APPLICABLE: "Non applicabile",
};

const HANDOFF_STATE_LABELS_IT = {
  NOT_READY: "Ci sono punti da chiarire",
  READY_FOR_PA: "Pronto per il Property Advisor",
  REASSESSMENT_REQUIRED: "Da rivedere prima del contatto",
  CLOSED_NO_HANDOFF: "Chiuso senza contatto",
};
